import type { InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import type { PiniaPluginContext } from 'pinia'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getConfig } from '@/common/config'
import * as oauth2Cookie from '@/common/oauth2/oauth2Cookie'

import {
  clearOauth2Persistence,
  getOauth2PersistStorageKey,
  getOauth2RememberMarkerKey,
  oauth2PersistStorage
} from './helper/oauth2PersistStorage'
import type { ExtendedJwtPayload, TokenInfo, TokenResponse } from './interface/oauth2Store.d'

// Create axios instance for OAuth2 requests
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
  }
})

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const configData = getConfig()
  if (configData?.oauth2?.endpoint) {
    config.baseURL = configData.oauth2.endpoint
  }
  return config
})

export const useOAuth2Store = defineStore(
  'oauth2Store',
  () => {
    // State
    const tokenInfo = ref<TokenInfo | null>(null)
    /** When true, tokens + cookie persist across browser restarts ("Remember me"). */
    const rememberMe = ref(false)
    const refreshTokenInProgress = ref<boolean>(false)
    const currentRoute = ref<{
      name?: string | symbol | null
      path: string
      query: Recordable
      params: Recordable
    } | null>(null)
    /** Avatar attachment u_id pulled from /auth/me; null when user has none. */
    const avatarAttachmentUid = ref<string | null>(null)

    // Getters
    const getterTokenInfo = computed(() => {
      return tokenInfo.value
    })

    const getterCurrentRoute = computed(() => {
      return currentRoute.value
    })

    const getterRefreshToken = computed(() => {
      return tokenInfo.value?.refreshToken ?? null
    })

    const getterCurrentUser = computed(() => {
      return tokenInfo.value?.token?.uid ?? null
    })

    const getterCurrentUserFirstName = computed(() => {
      return tokenInfo.value?.token?.first_name ?? null
    })

    const getterCurrentUserLastName = computed(() => {
      return tokenInfo.value?.token?.last_name ?? null
    })

    const getterCurrentUserEmail = computed(() => {
      return tokenInfo.value?.token?.mail ?? null
    })

    const getterCurrentUserFullName = computed(() => {
      const gn = (tokenInfo.value?.token?.given_name || tokenInfo.value?.token?.first_name || '').trim()
      const fn = (tokenInfo.value?.token?.family_name || tokenInfo.value?.token?.last_name || '').trim()
      return `${gn} ${fn}`.trim()
    })

    const getterCurrentUserUId = computed(() => {
      return tokenInfo.value?.token?.uid ?? null
    })

    const getterExpireTime = computed(() => {
      return tokenInfo.value?.expireTime ?? null
    })

    // Actions
    const login = async (username: string, password: string): Promise<TokenInfo> => {
      const configData = getConfig()
      const url = `${configData?.apiBaseUrl}auth/login`

      const formData = new URLSearchParams()
      formData.append('username', username)
      formData.append('password', password)

      try {
        const response = await axios.post<TokenResponse>(url, formData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })

        const data = response.data as TokenResponse & {
          password_reset_required?: boolean
          mfa_required?: boolean
          mfa_setup_required?: boolean
        }
        if (data.password_reset_required || data.mfa_required || data.mfa_setup_required) {
          const stepError = new Error('Login step required') as Error & {
            response?: { data: typeof data }
          }
          stepError.response = { data }
          throw stepError
        }

        const accessToken = response.data.access_token
        const token = jwtDecode<ExtendedJwtPayload>(accessToken)

        const now = new Date().getTime()
        const expiresIn = response.data.expires_in || 900
        const expireTime = now + expiresIn * 1000

        const rtExpiresIn = response.data.refresh_token_expires_in || 7 * 86400
        const refreshTokenExpireTime = now + rtExpiresIn * 1000

        tokenInfo.value = {
          accessToken,
          refreshToken: response.data.refresh_token,
          token,
          tokenType: response.data.token_type,
          expireTime,
          refreshTokenExpireTime
        }
        return tokenInfo.value
      } catch (error) {
        console.error('Login failed:', error)
        throw error
      }
    }

    const signin = (): Promise<string> => {
      try {
        const configData = getConfig()
        if (configData?.authMode === 'local') {
          return Promise.resolve('local')
        }
        if (!configData?.oauth2) {
          return Promise.reject(new Error('OAuth2 configuration is missing'))
        }

        const authUrl = configData.oauth2.authorization_url.replace('{tenant_id}', configData.oauth2.tenant_id)
        const params = new URLSearchParams({
          response_type: 'code',
          client_id: configData.oauth2.client_id,
          redirect_uri: configData.oauth2.redirect_uri,
          scope: configData.oauth2.scope.replace('{client_id}', configData.oauth2.client_id),
          sso_reload: 'true'
        })
        window.location.href = `${authUrl}?${params.toString()}`
        return Promise.resolve('success')
      } catch (error) {
        console.error('Error during login: ', error)
        return Promise.reject(new Error(String(error)))
      }
    }

    const handleCallback = async (_code: string): Promise<TokenInfo> => {
      return Promise.reject(
        new Error(
          'Legacy external OAuth token exchange is not supported by this backend. Use local sign-in or OIDC SSO (PKCE + /oauth-callback).'
        )
      )
    }

    let _refreshPromise: Promise<TokenInfo> | null = null

    const doRefreshToken = async (): Promise<TokenInfo> => {
      if (_refreshPromise) {
        return _refreshPromise
      }

      if (!tokenInfo.value?.refreshToken) {
        throw new Error('Refresh token is not available.')
      }

      const configData = getConfig()
      const authMode = configData?.authMode

      refreshTokenInProgress.value = true

      const execute = async (): Promise<TokenInfo> => {
        try {
          let response: import('axios').AxiosResponse<TokenResponse>

          if (authMode === 'local') {
            const url = `${configData?.apiBaseUrl}auth/refresh`
            response = await axios.post<TokenResponse>(url, {
              refresh_token: tokenInfo.value!.refreshToken
            })
          } else {
            response = await axiosInstance.post<TokenResponse>(
              'refresh',
              {
                refresh_token: tokenInfo.value!.refreshToken
              },
              {
                headers: { 'Content-Type': 'application/json' }
              }
            )
          }

          const accessToken = response.data.access_token
          const token = jwtDecode<ExtendedJwtPayload>(accessToken)

          const now = new Date().getTime()
          const expiresIn = response.data.expires_in || 900
          const rtExpiresIn = response.data.refresh_token_expires_in || 7 * 86400

          tokenInfo.value = {
            accessToken,
            refreshToken: response.data.refresh_token || tokenInfo.value!.refreshToken,
            token,
            tokenType: response.data.token_type,
            expireTime: now + expiresIn * 1000,
            refreshTokenExpireTime: now + rtExpiresIn * 1000
          }

          oauth2Cookie.set(true, rememberMe.value)
          return tokenInfo.value
        } catch (error) {
          console.error('Error refreshing token: ', error)
          tokenInfo.value = null
          throw error
        } finally {
          refreshTokenInProgress.value = false
          _refreshPromise = null
        }
      }

      _refreshPromise = execute()
      return _refreshPromise
    }

    const signout = (): Promise<void> => {
      console.log('In signout')
      clearOauth2Persistence(getOauth2PersistStorageKey())
      tokenInfo.value = null
      rememberMe.value = false
      refreshTokenInProgress.value = false
      avatarAttachmentUid.value = null
      return Promise.resolve()
    }

    const setAvatarAttachmentUid = (uid: string | null) => {
      avatarAttachmentUid.value = uid
    }

    const isAuthenticated = (): boolean => {
      const now = new Date().getTime()
      const ti = tokenInfo.value

      if (!ti) {
        return false
      }

      if (ti.expireTime && now < ti.expireTime) {
        return true
      }

      // Keep session alive for refresh flow when access token is expired.
      // Older persisted states may not contain refreshTokenExpireTime.
      if (!ti.refreshToken) {
        return false
      }

      if (!ti.refreshTokenExpireTime) {
        return true
      }

      return now < ti.refreshTokenExpireTime
    }

    return {
      // State
      tokenInfo,
      rememberMe,
      refreshTokenInProgress,
      currentRoute,
      avatarAttachmentUid,
      // Getters
      getterTokenInfo,
      getterCurrentRoute,
      getterRefreshToken,
      getterCurrentUser,
      getterCurrentUserFirstName,
      getterCurrentUserLastName,
      getterCurrentUserEmail,
      getterCurrentUserFullName,
      getterCurrentUserUId,
      getterExpireTime,
      // Actions
      login,
      signin,
      handleCallback,
      doRefreshToken,
      signout,
      isAuthenticated,
      setAvatarAttachmentUid
    }
  },
  {
    persist: {
      key: getOauth2PersistStorageKey(),
      storage: oauth2PersistStorage,
      pick: ['tokenInfo', 'rememberMe', 'refreshTokenInProgress', 'currentRoute', 'avatarAttachmentUid'],
      afterHydrate: (ctx: PiniaPluginContext) => {
        const marker = localStorage.getItem(getOauth2RememberMarkerKey())
        if (marker === 'persistent' || marker === 'session') {
          return
        }
        const persistKey = getOauth2PersistStorageKey()
        const raw = localStorage.getItem(persistKey) ?? sessionStorage.getItem(persistKey)
        if (!raw) {
          return
        }
        let parsed: Record<string, unknown>
        try {
          parsed = JSON.parse(raw)
        } catch {
          return
        }
        if (!('rememberMe' in parsed)) {
          const s = ctx.store as { tokenInfo?: { accessToken?: string } | null }
          if (s.tokenInfo?.accessToken) {
            ;(ctx.store as { $patch: (p: { rememberMe: boolean }) => void }).$patch({ rememberMe: true })
          }
        }
      }
    }
  }
)
